/**
 * Gemeinsame Anzeige-Logik für das Bestands-Änderungslog (stock_change_log) -
 * wird von der Tabellenseite (Pages/Stock/ChangeLog.jsx) UND dem
 * Artikel-Verlaufsfenster (Components/ArticleHistoryModal.jsx) genutzt, damit
 * ein Log-Eintrag an beiden Stellen exakt gleich beschriftet wird.
 */

export const SYSTEM_LABELS = {
    weclapp: "Weclapp",
    b2c: "B2C-Shop",
    b2b: "B2B-Shop",
};

export const SYSTEM_BADGE_VARIANT = {
    weclapp: "secondary",
    b2c: "primary",
    b2b: "info",
};

// Die technischen Werte bleiben "in"/"out" - hier steht nur, wie sie in der
// Oberfläche heißen. "In Zulauf" ist die verbindliche Bezeichnung für den
// Zustand, der früher "nicht auf Lager" hieß; sie gilt genauso im täglichen
// Bestandsbericht per E-Mail.
const AVAILABILITY_LABELS = {
    in: "Auf Lager",
    out: "In Zulauf",
};

const SOURCE_LABELS = {
    auto: "Automatischer Lauf",
    manual: "Manuell",
    backfill: "Übernommene Historie",
};

/**
 * "Weclapp" / "B2C-Shop" / "B2B-Shop" - Klartext-Kennzeichen für die
 * System-Spalte.
 *
 * @param {string|null|undefined} system
 * @returns {string}
 */
export function formatSystemLabel(system) {
    if (!system) {
        return "–";
    }
    return SYSTEM_LABELS[system] || system;
}

/**
 * Bootstrap-Badge-Variante je System, für die farbliche Unterscheidung in
 * der Tabelle.
 *
 * @param {string|null|undefined} system
 * @returns {string}
 */
export function systemBadgeVariant(system) {
    return SYSTEM_BADGE_VARIANT[system] || "light";
}

/**
 * Formatiert einen einzelnen Wert (alt ODER neu) je nach Änderungsart:
 * bei "availability" der Klartext ("Auf Lager"/"In Zulauf"), bei
 * "quantity" die reine Zahl. Ein fehlender Wert wird als "–" dargestellt,
 * NICHT als "0" - ein leeres old_value bedeutet "vorher nicht bekannt",
 * kein Bestand von 0.
 *
 * @param {string|null|undefined} value
 * @param {string} changeType - "quantity" | "availability"
 * @returns {string}
 */
export function formatChangeValue(value, changeType) {
    if (value === null || value === undefined || value === "") {
        return "–";
    }
    if (changeType === "availability") {
        return AVAILABILITY_LABELS[value] || value;
    }
    return value;
}

/**
 * "42 → 18 (SNV Weilheim)" bzw. "Auf Lager → In Zulauf" - die
 * vollständige Änderungs-Beschreibung EINES Log-Eintrags als reiner Text
 * (für die Zeitleiste im Verlaufsfenster).
 *
 * @param {{old_value: ?string, new_value: ?string, change_type: string, warehouse_name: ?string}} row
 * @returns {string}
 */
export function formatChangeText(row) {
    const oldLabel = formatChangeValue(row.old_value, row.change_type);
    const newLabel = formatChangeValue(row.new_value, row.change_type);
    const suffix = row.change_type === "quantity" && row.warehouse_name ? ` (${row.warehouse_name})` : "";
    return `${oldLabel} → ${newLabel}${suffix}`;
}

/**
 * "Automatischer Lauf" / "Manuell durch <Name>" / "Übernommene Historie" -
 * die Auslöser-Beschriftung EINES Log-Eintrags.
 *
 * @param {{source: string, user_name: ?string}} row
 * @returns {string}
 */
export function formatSourceText(row) {
    if (row.source === "manual") {
        return `Manuell durch ${row.user_name || "unbekannt"}`;
    }
    return SOURCE_LABELS[row.source] || row.source || "–";
}

/**
 * Deutsches Datum-plus-Uhrzeit-Format "07.08.2026, 14:15" - eigenständig von
 * StatusTimestampControl (dort ist ein anderes Format samt "Uhr"-Suffix
 * vorgegeben), damit diese Tabelle nicht heimlich mitbricht, wenn dort das
 * Format geändert wird.
 *
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function formatLogTimestamp(value) {
    if (!value) {
        return "–";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "–";
    }
    const datePart = date.toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit", year: "numeric"});
    const timePart = date.toLocaleTimeString("de-DE", {hour: "2-digit", minute: "2-digit"});
    return `${datePart}, ${timePart}`;
}
